(window.requestIdleCallback || function(cb) { setTimeout(cb, 1); })(function() {
(function() {
  'use strict';

  const config = window.KlaviyoEnhancedConfig;

  // The inline config renders just before this script, yet search pages have started this
  // without it, some mid-redirect (SHOPIFY-22R, #2083) and some that stayed put with the
  // config tag on the page (SHOPIFY-22Z, #2226). Stay off, and tell Sentry what the page
  // looked like so the next one shows why. The message must not say "klaviyo":
  // sentry-config's ignoreErrors drops any message matching /klaviyo/i.
  if (!config) {
    if (window.Sentry?.captureMessage) {
      const configProperty = Object.getOwnPropertyDescriptor(window, 'KlaviyoEnhancedConfig');
      const uaData = navigator.userAgentData;
      window.Sentry.captureMessage('Enhanced tracking started without its page config', {
        level: 'warning',
        tags: { component: 'klaviyo-enhanced' },
        extra: {
          documentURL: document.URL,
          locationHref: location.href,
          readyState: document.readyState,
          visibilityState: document.visibilityState,
          configTagOnPage: Array.prototype.some.call(document.scripts, function(s) {
            return !s.src && /KlaviyoEnhancedConfig\s*=\s*\{/.test(s.textContent);
          }),
          // The same inline script sets all three. All missing means it never ran; the
          // other two present means the config alone was removed or overridden after.
          customerDataSet: typeof window.KlaviyoCustomerData !== 'undefined',
          pageContextSet: typeof window.KlaviyoPageContext !== 'undefined',
          // 'accessor' is a getter planted on window, how blocker scriptlets pin a global.
          configProperty: !configProperty ? 'absent' : ('get' in configProperty ? 'accessor' : 'data'),
          // Brave's user agent reads as plain Chrome.
          browserBrands: uaData && Array.isArray(uaData.brands)
            ? uaData.brands.map(function(b) { return b.brand + ' ' + b.version; })
            : null,
          brave: !!navigator.brave
        }
      });
    }
    return;
  }

  const customerData = window.KlaviyoCustomerData || {};
  const pageContext = window.KlaviyoPageContext || {};

  // Shopify's Customer Privacy API decides, on analytics and marketing. Until it has
  // loaded the answer is 'pending': nothing is sent or stored, and tracking starts only
  // once it resolves (whenConsentKnown). While the shopper has declined, nothing is sent,
  // no identify is pushed, and no queue is kept on the device. klaviyo-tracking.js
  // applies the same rules.
  function consentStatus() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return 'pending';
    // Profiles feed marketing email, so a marketing decline stops it too.
    const refused = privacy.analyticsProcessingAllowed() === false ||
      (typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed() === false);
    if (!refused) return 'allowed';
    // Where consent is required, "not allowed" also means the banner is still waiting
    // for an answer. That is pending, not a decline.
    const unanswered = typeof privacy.shouldShowBanner === 'function' && privacy.shouldShowBanner();
    return unanswered ? 'pending' : 'declined';
  }

  function analyticsAllowed() {
    return consentStatus() === 'allowed';
  }

  function whenConsentKnown(callback) {
    let done = false;
    let attempts = 0;
    function check() {
      if (done || consentStatus() === 'pending') return false;
      done = true;
      callback();
      return true;
    }
    if (check()) return;

    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], check);
    }
    document.addEventListener('visitorConsentCollected', check);
    (function poll() {
      if (check() || ++attempts >= 100) return;
      setTimeout(poll, 100);
    })();
  }

  // ============================================================================
  // KLAVIYO QUEUE CLASS
  // ============================================================================

  const REPLAY_ATTEMPTS = 5;

  // Events sent only to a known shopper, never queued. theme.js publishes cartUpdate on
  // every page load, so a queued Cart Updated per page would crowd real events out of the
  // queue; LDC - Cart Snapshot carries the cart at signup instead.
  const LIVE_ONLY = ['Cart Updated'];

  const KlaviyoQueue = {
    STORAGE_KEY: 'klaviyo_event_queue',
    data: null,

    /**
     * Initialize the queue
     */
    init: function() {
      this.data = this.load();
      this.ensureAnonymousId();
      this.cleanupOldEvents();
      this.listenForStorageChanges();
      KlaviyoEnhanced.log('Queue initialized:', this.data.anonymousId, 'Events:', this.data.events.length);
    },

    /**
     * Load queue from localStorage
     */
    load: function() {
      const defaultData = {
        anonymousId: null,
        createdAt: new Date().toISOString(),
        identifiedEmail: null,
        events: []
      };

      if (!config.queue.enabled || !this.hasLocalStorage() || !analyticsAllowed()) {
        return defaultData;
      }

      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          // Validate structure
          if (parsed && typeof parsed === 'object' && Array.isArray(parsed.events)) {
            return parsed;
          }
        }
      } catch (e) {
        KlaviyoEnhanced.log('Failed to load queue:', e);
      }

      return defaultData;
    },

    /**
     * Save queue to localStorage
     */
    save: function() {
      if (!config.queue.enabled || !this.hasLocalStorage()) return;
      if (consentStatus() === 'declined') {
        localStorage.removeItem(this.STORAGE_KEY);
        return;
      }
      if (!analyticsAllowed()) return;

      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.data));
      } catch (e) {
        KlaviyoEnhanced.log('Failed to save queue:', e);
        // If storage full, remove oldest events
        if (e.name === 'QuotaExceededError') {
          this.data.events = this.data.events.slice(-50);
          try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.data));
          } catch (e2) {
            KlaviyoEnhanced.log('Still failed to save queue:', e2);
          }
        }
      }
    },

    /**
     * Check if localStorage is available
     */
    hasLocalStorage: function() {
      try {
        const key = 'klaviyo_test';
        localStorage.setItem(key, 'test');
        localStorage.removeItem(key);
        return true;
      } catch (e) {
        return false;
      }
    },

    /**
     * Ensure we have an anonymous ID
     */
    ensureAnonymousId: function() {
      if (!this.data.anonymousId) {
        this.data.anonymousId = 'kq_' + this.generateId();
        this.data.createdAt = new Date().toISOString();
        this.save();
      }
    },

    /**
     * Generate unique ID
     */
    generateId: function() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    },

    /**
     * Generate event ID for deduplication
     */
    generateEventId: function() {
      return 'evt_' + this.generateId();
    },

    /**
     * Clean up old events based on max age
     */
    cleanupOldEvents: function() {
      const maxAge = config.queue.maxAgeDays * 24 * 60 * 60 * 1000;
      const now = Date.now();
      const originalCount = this.data.events.length;

      this.data.events = this.data.events.filter(function(event) {
        const eventTime = new Date(event.timestamp).getTime();
        return (now - eventTime) < maxAge;
      });

      if (this.data.events.length !== originalCount) {
        KlaviyoEnhanced.log('Cleaned up', originalCount - this.data.events.length, 'old events');
        this.save();
      }
    },

    /**
     * Trim events to max size
     */
    trimEvents: function() {
      if (this.data.events.length > config.queue.maxEvents) {
        this.data.events = this.data.events.slice(-config.queue.maxEvents);
      }
    },

    /**
     * Check if user is identified
     */
    isIdentified: function() {
      return !!this.data.identifiedEmail || !!this.getKlaviyoIdentifiedEmail();
    },

    /**
     * Get identified email from various sources
     */
    getKlaviyoIdentifiedEmail: function() {
      // 1. Shopify customer (logged in). Checked before the stored email: on a shared
      // device the stored one can belong to whoever identified there last.
      if (customerData.isLoggedIn && customerData.email) {
        return customerData.email;
      }

      // 2. Check our stored email
      if (this.data.identifiedEmail) {
        return this.data.identifiedEmail;
      }

      // 3. Check Klaviyo's __kla_id cookie
      try {
        const klaCookie = this.getCookie('__kla_id');
        if (klaCookie) {
          const decoded = JSON.parse(atob(decodeURIComponent(klaCookie)));
          if (decoded && decoded.$email) {
            return decoded.$email;
          }
        }
      } catch (e) {
        // Cookie parsing failed, continue
      }

      return null;
    },

    /**
     * Klaviyo's encrypted profile token from the __kla_id cookie. klaviyo.js sets it when a
     * shopper arrives from a Klaviyo email; the email itself never reaches the device.
     */
    getKlaviyoExchangeId: function() {
      try {
        const klaCookie = this.getCookie('__kla_id');
        if (!klaCookie) return null;
        const decoded = JSON.parse(atob(decodeURIComponent(klaCookie)));
        return (decoded && decoded.$exchange_id) || null;
      } catch {
        return null;
      }
    },

    /**
     * Get cookie value
     */
    getCookie: function(name) {
      const value = '; ' + document.cookie;
      const parts = value.split('; ' + name + '=');
      if (parts.length === 2) {
        return parts.pop().split(';').shift();
      }
      return null;
    },

    /**
     * Track an event
     */
    track: function(eventName, properties) {
      if (!analyticsAllowed()) return;
      const email = this.getKlaviyoIdentifiedEmail();

      if (email) {
        // User is identified, send directly
        this.sendToKlaviyo(eventName, properties, email);
        return;
      }

      if (!config.queue.enabled || LIVE_ONLY.indexOf(eventName) !== -1) {
        // Queue disabled or a live-only event, and user not identified: skip
        KlaviyoEnhanced.log('Skipping event (queue disabled, user anonymous):', eventName);
        return;
      }

      // Queue the event for later
      const event = {
        id: this.generateEventId(),
        event: eventName,
        properties: properties,
        timestamp: new Date().toISOString(),
        sent: false,
        pageUrl: window.location.pathname
      };

      this.data.events.push(event);
      this.trimEvents();
      this.save();

      KlaviyoEnhanced.log('Event queued:', eventName, '(Total queued:', this.data.events.length + ')');
    },

    /**
     * Send event to Klaviyo
     */
    sendToKlaviyo: function(eventName, properties, email) {
      if (!analyticsAllowed()) return;
      const _learnq = window._learnq || [];

      // Ensure user is identified
      if (email) {
        _learnq.push(['identify', { $email: email }]);
      }

      // Build event properties with $value for revenue tracking. Replayed tells flows a
      // live event from one replayed at signup (see replay).
      const eventProps = Object.assign({}, properties, { Replayed: false });
      if (properties.Price) {
        eventProps.$value = properties.Price;
      } else if (properties.cart_total) {
        eventProps.$value = properties.cart_total;
      }

      _learnq.push(['track', eventName, eventProps]);
      KlaviyoEnhanced.log('Event sent to Klaviyo:', eventName, eventProps);
    },

    /**
     * Handle user identification
     */
    onIdentify: function(email) {
      if (!email || this.data.identifiedEmail === email || !analyticsAllowed()) return;

      KlaviyoEnhanced.log('User identified:', email, 'Replaying', this.data.events.length, 'queued events');

      const _learnq = window._learnq || [];

      // Set identifiedEmail BEFORE push to prevent recursion from interceptor
      this.data.identifiedEmail = email;
      // Remembered so checkExistingIdentity can forget the email once they log out.
      this.data.identifiedLoggedIn = !!(customerData.isLoggedIn && customerData.email === email);

      // Identify with Klaviyo
      _learnq.push(['identify', { $email: email }]);

      this.save();
      this.replay(email);
      document.dispatchEvent(new CustomEvent('klaviyo-enhanced:identified', { detail: { email: email } }));
    },

    /**
     * Replay events queued while anonymous with their original time, through Klaviyo's
     * Client API; _learnq would stamp every one with the signup time. Each carries its
     * queue id as unique_id, so Klaviyo dedupes a retry. backfill: true records it without
     * triggering flows, so a view from weeks ago can't start browse abandon now; Replayed:
     * true marks it for segments. An event leaves the queue once Klaviyo accepts it, or
     * rejects it outright (a 4xx other than 429); network errors, 5xx and 429 are retried
     * on later pages, REPLAY_ATTEMPTS times at most.
     */
    replay: function(email) {
      const self = this;
      const pending = this.data.events.filter(function(event) { return !event.sent; });
      if (!pending.length || !analyticsAllowed() || this.replaying) return;
      if (!config.companyId) {
        console.warn('[Klaviyo Enhanced] No Klaviyo public site ID set (view_in_room_klaviyo_company_id); queued events are not replayed.');
        return;
      }
      this.replaying = true;
      const done = {};
      let rejected = 0;

      Promise.all(pending.map(function(event) {
        const properties = Object.assign({}, event.properties, {
          Replayed: true,
          _original_timestamp: event.timestamp,
          _queued_hours: self.getHoursSince(event.timestamp),
          _was_anonymous: true,
          _anonymous_id: self.data.anonymousId
        });
        const attributes = {
          properties: properties,
          time: event.timestamp,
          unique_id: event.id,
          metric: { data: { type: 'metric', attributes: { name: event.event } } },
          profile: { data: { type: 'profile', attributes: { email: email } } }
        };
        attributes.backfill = true;
        const value = properties.Price || properties.cart_total;
        if (value) {
          attributes.value = value;
          const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
          if (currency) attributes.value_currency = currency;
        }

        return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(config.companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify({ data: { type: 'event', attributes: attributes } })
        }).then(function(response) {
          if (response.ok) {
            done[event.id] = true;
          } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            done[event.id] = true;
            rejected++;
          } else {
            event.attempts = (event.attempts || 0) + 1;
          }
        }).catch(function() {
          event.attempts = (event.attempts || 0) + 1;
        });
      })).then(function() {
        self.replaying = false;
        self.data.events = self.data.events.filter(function(event) {
          return !done[event.id] && (event.attempts || 0) < REPLAY_ATTEMPTS;
        });
        self.save();
        if (rejected) console.warn('[Klaviyo Enhanced] Klaviyo rejected', rejected, 'replayed events; they were dropped.');
        KlaviyoEnhanced.log('Replayed', Object.keys(done).length - rejected, 'of', pending.length, 'queued events');
      });
    },

    /**
     * Calculate hours since timestamp
     */
    getHoursSince: function(timestamp) {
      const eventTime = new Date(timestamp).getTime();
      const now = Date.now();
      return Math.round((now - eventTime) / (1000 * 60 * 60) * 10) / 10;
    },

    /**
     * Listen for localStorage changes (cross-tab sync)
     */
    listenForStorageChanges: function() {
      const self = this;
      window.addEventListener('storage', function(e) {
        if (e.key === self.STORAGE_KEY) {
          // Another tab updated the queue
          self.data = self.load();
          KlaviyoEnhanced.log('Queue synced from another tab');
        }
      });
    },

    /**
     * Clear queue (for logout or GDPR)
     */
    clear: function() {
      this.data.events = [];
      this.data.identifiedEmail = null;
      this.save();
      KlaviyoEnhanced.log('Queue cleared');
    }
  };

  // ============================================================================
  // KLAVIYO ENHANCED TRACKING
  // ============================================================================

  var KlaviyoEnhanced = {
    initialized: false,
    quickViewOpenTime: null,
    lastSearchQuery: null,

    /**
     * Initialize tracking
     */
    init: function() {
      const self = this;
      let attempts = 0;

      function tryInit() {
        attempts++;

        // Check if Klaviyo SDK is loaded
        if (typeof window._learnq !== 'undefined' || attempts >= config.retryAttempts) {
          if (typeof window._learnq === 'undefined') {
            window._learnq = window._learnq || [];
            self.log('Klaviyo SDK not found, using fallback queue');
          }

          // Initialize queue
          KlaviyoQueue.init();

          // Check for existing identification
          self.checkExistingIdentity();

          // Set up all tracking
          self.setupTracking();
          self.initialized = true;

          self.log('Klaviyo Enhanced Tracking initialized');
          document.dispatchEvent(new CustomEvent('klaviyo-enhanced:ready'));
        } else {
          setTimeout(tryInit, config.retryInterval);
        }
      }

      whenConsentKnown(function() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', tryInit);
        } else {
          tryInit();
        }
      });
    },

    /**
     * Check for existing identity on page load
     */
    checkExistingIdentity: function() {
      // A logged-in customer's email stays on the device after they log out; the next
      // person's activity must not go to them. Klaviyo's own __kla_id cookie also survives
      // a Shopify logout, and the theme can't clear that.
      if (!customerData.isLoggedIn && KlaviyoQueue.data.identifiedLoggedIn) {
        this.log('Customer logged out; forgetting their email');
        KlaviyoQueue.clear();
        KlaviyoQueue.data.identifiedLoggedIn = false;
        KlaviyoQueue.save();
      }

      const email = KlaviyoQueue.getKlaviyoIdentifiedEmail();
      if (email && email !== KlaviyoQueue.data.identifiedEmail) {
        this.log('Existing identity detected:', email);
        KlaviyoQueue.onIdentify(email);
      } else if (email) {
        KlaviyoQueue.replay(email);
      }
    },

    /**
     * Set up all tracking listeners
     */
    setupTracking: function() {
      this.setupIdentificationListeners();
      this.setupPubSubListeners();
      this.setupDOMListeners();
      this.trackPageView();
    },

    // ==========================================================================
    // IDENTIFICATION LISTENERS
    // ==========================================================================

    setupIdentificationListeners: function() {
      const self = this;

      // 1. Newsletter signups. Only forms where shoppers give their own email to hear from
      // us: identifying attaches their cart, wishlist and views to that profile, and other
      // forms take someone else's (gift card recipient) or aren't a signup (contact, quote,
      // product questions, blog comments). Klaviyo forms, back in stock, logged-in
      // customers and the __kla_id cookie identify through their own paths below.
      document.addEventListener('submit', function(e) {
        const form = e.target;
        if (!form.matches('form[is="newsletter-form"], .newsletter-form form')) return;
        const emailInput = form.querySelector('input[type="email"], input[name="email"], input[name="contact[email]"]');
        if (emailInput && emailInput.value && emailInput.checkValidity()) {
          // Delay to allow form to process
          setTimeout(function() {
            KlaviyoQueue.onIdentify(emailInput.value);
          }, 500);
        }
      });

      // 2. Listen for Klaviyo popup form events
      window.addEventListener('klaviyoForms', function(e) {
        if (e.detail && e.detail.type === 'submit' && e.detail.metaData) {
          const email = e.detail.metaData.$email || e.detail.metaData.email;
          if (email) {
            KlaviyoQueue.onIdentify(email);
          }
        }
      });

      // 3. lamps.com capture signups (lamps-capture.js), including one made before this ran
      if (window.__lampsCaptureSignupEmail) KlaviyoQueue.onIdentify(window.__lampsCaptureSignupEmail);
      document.addEventListener('lamps-capture:signup', function(e) {
        if (e.detail && e.detail.email) KlaviyoQueue.onIdentify(e.detail.email);
      });

      // 4. Listen for Klaviyo identify calls
      this.interceptKlaviyoIdentify();

      // 5. Check periodically for Klaviyo cookie (backup)
      setInterval(function() {
        if (!KlaviyoQueue.data.identifiedEmail) {
          const email = KlaviyoQueue.getKlaviyoIdentifiedEmail();
          if (email) {
            KlaviyoQueue.onIdentify(email);
          }
        }
      }, 5000);

      this.log('Identification listeners registered');
    },

    /**
     * Intercept Klaviyo identify calls
     */
    interceptKlaviyoIdentify: function() {
      const originalPush = (window._learnq || []).push;
      const _learnq = window._learnq = window._learnq || [];

      _learnq.push = function() {
        const args = Array.prototype.slice.call(arguments);
        args.forEach(function(arg) {
          if (Array.isArray(arg) && arg[0] === 'identify' && arg[1] && arg[1].$email) {
            KlaviyoQueue.onIdentify(arg[1].$email);
          }
        });
        return originalPush.apply(_learnq, arguments);
      };
    },

    // ==========================================================================
    // PUBSUB LISTENERS
    // ==========================================================================

    setupPubSubListeners: function() {
      const self = this;

      if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub) {
        this.log('FoxTheme.pubsub not available');
        return;
      }

      const events = FoxTheme.pubsub.PUB_SUB_EVENTS;

      // Cart Update (Tier 2)
      if (config.tracking.cartUpdated) {
        FoxTheme.pubsub.subscribe(events.cartUpdate, function(event) {
          if (!event || !event.cart) return;

          const cart = event.cart;
          const items = (cart.items || []).map(function(item) {
            return {
              ProductID: String(item.product_id),
              ProductName: item.product_title,
              VariantID: String(item.variant_id),
              Price: item.price / 100,
              quantity: item.quantity
            };
          });

          self.track('Cart Updated', {
            cart_total: cart.total_price / 100,
            item_count: cart.item_count,
            items: items
          });
        });
      }

      // Cart Error (Tier 3 - Negative Signals)
      if (config.tracking.negativeSignals) {
        FoxTheme.pubsub.subscribe(events.cartError, function(event) {
          if (!event) return;

          let errorType = 'unknown';
          if (event.errors) {
            errorType = typeof event.errors === 'string'
              ? event.errors.substring(0, 50).replace(/[^\w\s-]/g, '')
              : 'validation_error';
          }

          // Try to get product data from pageContext or event
          const product = pageContext.product || {};

          self.track('Add to Cart Failed', {
            ProductID: String(product.id || event.productId || ''),
            ProductName: product.title || '',
            VariantID: String(event.productVariantId || ''),
            reason: errorType,
            source: event.source || 'unknown'
          });
        });
      }

      // Facet Update (Tier 2)
      if (config.tracking.filters) {
        FoxTheme.pubsub.subscribe(events.facetUpdate, function() {
          const params = new URLSearchParams(window.location.search);
          const filters = {};
          let filterCount = 0;

          params.forEach(function(value, key) {
            if (key.startsWith('filter.')) {
              const filterName = key.replace('filter.', '');
              filters[filterName] = value;
              filterCount++;
            }
          });

          if (filterCount > 0) {
            const collection = pageContext.collection || {};
            self.track('Filter Applied', {
              collection_handle: collection.handle || '',
              collection_title: collection.title || '',
              filter_type: Object.keys(filters).join(', '),
              filter_value: Object.values(filters).join(', '),
              filter_count: filterCount,
              sort_by: params.get('sort_by') || 'default'
            });
          }
        });
      }

      this.log('Pub/sub listeners registered');
    },

    // ==========================================================================
    // DOM LISTENERS
    // ==========================================================================

    setupDOMListeners: function() {
      const self = this;

      // Quick View Open (Tier 1)
      if (config.tracking.quickView) {
        document.addEventListener('quick-view:open', function(event) {
          self.quickViewOpenTime = Date.now();

          const productUrl = event.detail ? event.detail.productUrl : null;
          let productHandle = null;

          if (productUrl) {
            const match = productUrl.match(/\/products\/([^\/\?]+)/);
            productHandle = match ? match[1] : null;
          }

          // Get product data from the event or page context
          const productData = event.detail || {};

          self.track('Quick View Opened', {
            ProductID: String(productData.productId || ''),
            ProductName: productData.productTitle || '',
            ProductURL: self.getFullUrl(productUrl || ''),
            ImageURL: productData.imageUrl || '',
            Price: productData.price ? productData.price / 100 : 0,
            source_collection: pageContext.collection ? pageContext.collection.handle : '',
            trigger_element: 'product_card'
          });
        });

        // Quick View Close
        document.addEventListener('quick-view:close', function(event) {
          const timeOpen = self.quickViewOpenTime ? Math.round((Date.now() - self.quickViewOpenTime) / 1000) : 0;
          const productData = event.detail || {};

          self.track('Quick View Closed', {
            ProductID: String(productData.productId || ''),
            time_open_seconds: timeOpen,
            action_taken: productData.action || 'closed'
          });

          self.quickViewOpenTime = null;
        });
      }

      // Add to Cart (Tier 1)
      if (config.tracking.addedToCart) {
        document.addEventListener('cart:item-added', function(event) {
          const item = event.detail || {};

          self.track('Added to Cart', {
            ProductID: String(item.product_id || ''),
            ProductName: item.product_title || item.title || '',
            ProductURL: self.getFullUrl(item.url || ''),
            ImageURL: item.image || (item.featured_image ? item.featured_image.url : ''),
            Price: item.price ? item.price / 100 : 0,
            VariantID: String(item.variant_id || ''),
            SKU: item.sku || '',
            quantity: item.quantity || 1,
            source: item.source || pageContext.template || 'unknown'
          });
        });

        // Fallback: Listen for form submissions on add to cart buttons
        document.addEventListener('submit', function(e) {
          const form = e.target;
          if (!form.matches('form[action*="/cart/add"]')) return;

          // Extract product data from form
          const productId = form.querySelector('[name="id"]');
          if (!productId) return;

          // Use page context for product details on PDP
          if (pageContext.product) {
            self.track('Added to Cart', {
              ProductID: String(pageContext.product.id),
              ProductName: pageContext.product.title,
              ProductURL: self.getFullUrl(pageContext.product.url),
              ImageURL: pageContext.product.imageUrl || '',
              Price: pageContext.product.variantPrice || pageContext.product.price,
              VariantID: String(pageContext.product.variantId || productId.value),
              SKU: pageContext.product.variantSku || '',
              quantity: parseInt(form.querySelector('[name="quantity"]')?.value || 1, 10),
              source: 'pdp'
            });
          }
        });
      }

      // Search (Tier 2). AlgoliaShared.reportSearch records each finished search
      // in window.lampsSearches; ones from before tracking started are replayed.
      if (config.tracking.search) {
        const trackSearch = function(search) {
          self.lastSearchQuery = search.query;
          const properties = { query: search.query, results_count: search.resultsCount };
          if (search.redirected) properties.redirected = true;
          self.track('Search Performed', properties);
        };
        (window.lampsSearches || []).forEach(trackSearch);
        document.addEventListener('lamps:search', function(e) {
          trackSearch(e.detail);
        });

        // Search result click
        document.addEventListener('click', function(e) {
          const productLink = e.target.closest('.ais-Hits-item a[href*="/products/"]');
          if (!productLink || !self.lastSearchQuery) return;

          const hitItem = productLink.closest('.ais-Hits-item');
          const position = hitItem ? Array.from(hitItem.parentNode.children).indexOf(hitItem) + 1 : 0;

          const urlMatch = productLink.href.match(/\/products\/([^\/\?]+)/);
          const productHandle = urlMatch ? urlMatch[1] : '';

          self.track('Search Result Clicked', {
            query: self.lastSearchQuery,
            product_handle: productHandle,
            ProductURL: productLink.href,
            position: position
          });
        });
      }

      // Product Engagement (Tier 3)
      if (config.tracking.productEngagement) {
        // Image zoom
        document.addEventListener('photoswipe:open', function(event) {
          if (!pageContext.product) return;
          self.track('Product Image Zoomed', {
            ProductID: String(pageContext.product.id),
            ProductName: pageContext.product.title,
            image_index: event.detail?.index || 0,
            zoom_type: 'photoswipe'
          });
        });

        // Video played
        document.addEventListener('play', function(e) {
          if (!e.target.matches('video')) return;
          if (!pageContext.product) return;

          self.track('Product Video Played', {
            ProductID: String(pageContext.product.id),
            ProductName: pageContext.product.title,
            video_id: e.target.id || 'main',
            duration: e.target.duration || 0
          });
        }, true);

        // Size guide opened
        document.addEventListener('click', function(e) {
          const sizeGuideLink = e.target.closest('[data-size-guide], .size-guide-link, .size-chart-link');
          if (!sizeGuideLink) return;
          if (!pageContext.product) return;

          self.track('Size Guide Opened', {
            ProductID: String(pageContext.product.id),
            ProductName: pageContext.product.title,
            product_type: pageContext.product.type || ''
          });
        });

        // Reviews section viewed
        let reviewsTracked = false;
        const observer = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting && !reviewsTracked && pageContext.product) {
              reviewsTracked = true;
              self.track('Reviews Section Viewed', {
                ProductID: String(pageContext.product.id),
                ProductName: pageContext.product.title
              });
            }
          });
        }, { threshold: 0.5 });

        // Use specific selectors for the actual reviews section, not the star rating badge
        // .jdgm-rev-widg is the review widget container, .jdgm-review-widget is the full widget
        // Avoid .jdgm-widget as it also matches the star badge at top of page
        const reviewsSection = document.querySelector('#judgeme_product_reviews, .jdgm-rev-widg, .jdgm-review-widget, [data-reviews-section]');
        if (reviewsSection) {
          observer.observe(reviewsSection);
        }
      }

      // Negative Signals (Tier 3)
      if (config.tracking.negativeSignals) {
        // Out of stock viewed
        if (pageContext.product && !pageContext.product.available) {
          this.track('Out of Stock Viewed', {
            ProductID: String(pageContext.product.id),
            ProductName: pageContext.product.title,
            ProductURL: this.getFullUrl(pageContext.product.url)
          });
        }

        // Back in stock subscription
        document.addEventListener('submit', function(e) {
          const form = e.target;
          if (!form.matches('[data-back-in-stock-form], .bis-form, .klaviyo-bis-form')) return;

          const email = form.querySelector('input[type="email"]')?.value;
          const variantId = form.querySelector('[name="variant"]')?.value || form.dataset.variantId;

          // Try to get product data from pageContext or from form's closest product container
          let product = pageContext.product;
          if (!product || !product.id) {
            const productContainer = form.closest('[data-product-id]');
            if (productContainer) {
              product = {
                id: productContainer.dataset.productId,
                title: productContainer.querySelector('.product__title, h1, [data-product-title]')?.textContent?.trim() || ''
              };
            }
          }

          self.track('Back in Stock Subscribed', {
            ProductID: String(product?.id || ''),
            ProductName: product?.title || '',
            ProductURL: self.getFullUrl(product?.url || window.location.pathname),
            VariantID: String(variantId || ''),
            email_provided: !!email
          });

          if (email) {
            KlaviyoQueue.onIdentify(email);
          }
        });
      }

      // Marketing (Tier 4)
      if (config.tracking.marketing) {
        // Newsletter signup
        document.addEventListener('submit', function(e) {
          const form = e.target;
          if (!form.matches('form[is="newsletter-form"], .newsletter-form form, .klaviyo-form')) return;

          const emailInput = form.querySelector('input[type="email"]');
          if (!emailInput || !emailInput.value) return;

          self.track('Newsletter Subscribed', {
            email_provided: true,
            source: self.getNewsletterSource(form)
          });
        });
      }

      this.log('DOM listeners registered');
    },

    // ==========================================================================
    // PAGE VIEW TRACKING
    // ==========================================================================

    trackPageView: function() {
      const self = this;

      // Collection Viewed (Tier 1)
      if (config.tracking.collectionViewed && pageContext.collection) {
        const collection = pageContext.collection;
        const params = new URLSearchParams(window.location.search);

        this.track('Collection Viewed', {
          collection_handle: collection.handle,
          collection_title: collection.title,
          product_count: collection.productCount,
          sort_order: params.get('sort_by') || 'default',
          page_number: parseInt(params.get('page') || 1, 10)
        });
      }

      // Cart Viewed (Tier 2)
      if (config.tracking.cartViewed && pageContext.template === 'cart') {
        // Fetch cart data
        fetch('/cart.js')
          .then(function(response) { return response.json(); })
          .then(function(cart) {
            const items = cart.items.map(function(item) {
              return {
                ProductID: String(item.product_id),
                ProductName: item.product_title,
                ProductURL: self.getFullUrl(item.url),
                ImageURL: item.image,
                Price: item.price / 100,
                VariantID: String(item.variant_id),
                quantity: item.quantity
              };
            });

            self.track('Cart Viewed', {
              cart_total: cart.total_price / 100,
              item_count: cart.item_count,
              items: items
            });
          })
          .catch(function(err) {
            self.log('Failed to fetch cart:', err);
          });
      }

      // Account events (Tier 4)
      if (config.tracking.account) {
        // Account login detected
        if (customerData.isLoggedIn && pageContext.template === 'customers/account') {
          this.track('Account Login', {
            customer_id: String(customerData.id),
            orders_count: customerData.ordersCount,
            total_spent: customerData.totalSpent
          });
        }
      }
    },

    // ==========================================================================
    // HELPER METHODS
    // ==========================================================================

    /**
     * Track an event with normalized properties
     */
    track: function(eventName, properties) {
      const normalizedProps = this.normalizeProperties(properties);
      KlaviyoQueue.track(eventName, normalizedProps);
    },

    /**
     * Normalize properties to Klaviyo format (PascalCase for product fields)
     */
    normalizeProperties: function(props) {
      const normalized = {};

      // Copy all properties
      Object.keys(props).forEach(function(key) {
        // Skip null/undefined
        if (props[key] == null) return;

        normalized[key] = props[key];
      });

      // Ensure required product fields are present and properly formatted
      if (normalized.ProductID) {
        normalized.ProductID = String(normalized.ProductID);
      }
      if (normalized.VariantID) {
        normalized.VariantID = String(normalized.VariantID);
      }
      if (normalized.Price !== undefined) {
        normalized.Price = parseFloat(normalized.Price) || 0;
      }
      if (normalized.CompareAtPrice !== undefined && normalized.CompareAtPrice !== null) {
        normalized.CompareAtPrice = parseFloat(normalized.CompareAtPrice) || null;
      }

      // Add sourceUrl to all events
      normalized.sourceUrl = window.location.href;

      return normalized;
    },

    /**
     * Get full URL
     */
    getFullUrl: function(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      return window.location.origin + path;
    },

    /**
     * Get newsletter source from form context
     */
    getNewsletterSource: function(form) {
      const section = form.closest('[data-section-type], .shopify-section');
      if (section) {
        if (section.classList.contains('section-footer')) return 'footer';
        if (section.classList.contains('section-popup') || section.id?.includes('popup')) return 'popup';
        if (section.classList.contains('section-newsletter')) return 'dedicated_section';
      }
      return 'unknown';
    },

    /**
     * Debug logging
     */
    log: function() {
      if (config.debug) {
        const args = Array.prototype.slice.call(arguments);
        args.unshift('[Klaviyo Enhanced]');
        console.log.apply(console, args);
      }
    }
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Exposed before init: klaviyo-tracking.js reads identity from KlaviyoQueue as soon as
  // the ready event fires, and init can fire it synchronously.
  window.KlaviyoEnhanced = KlaviyoEnhanced;
  window.KlaviyoQueue = KlaviyoQueue;

  KlaviyoEnhanced.init();

  // Global helper function
  window.klaviyoTrack = function(event, props) {
    KlaviyoQueue.track(event, props);
  };

})();
}, {timeout: 3000});
